import { EllipsisTooltip } from 'App/Map/PanelDetails/OverviewCharts/EllipsisTooltip'
import type {
  CurrentValuesDataInfo,
  TInsectTrapCatchesKey,
} from 'App/Map/PanelDetails/OverviewCharts/OverviewColumns'
import { basicSort, pestSorter } from 'App/Map/PanelDetails/OverviewCharts/SectionTable/utils'
import { lastDays, sinceFirstOf } from 'App/Map/PanelDetails/OverviewCharts/_utils/columnTitles'
import type { TValueGroupColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/types'
import type { TColumn } from 'components/SummaryTable/SummaryTable.types'
import { translate } from 'i18n/i18n'
import { getInsectNameFromId } from 'utils/getInsectNameFromId'
import { unitConverter } from 'utils/unitConverter/unitConverter'

const findTrapCatchesKey = (record: CurrentValuesDataInfo) =>
  Object.keys(record).find((key) => key.startsWith('insectTrapCatches_') && key.includes('insectId'))

const getPestName = (record: CurrentValuesDataInfo) => {
  const insectId = findTrapCatchesKey(record)?.match(/insectId(\d+)/)?.[1]

  return getInsectNameFromId(insectId ? Number(insectId) : -1)
}

const trapCatchesColumn = (prefix: string, title: string): TColumn => ({
  title: <EllipsisTooltip fullText={title} />,
  dataIndex: `${prefix}_insectId`,
  key: `${prefix}_insectId`,
  width: 150,
  render: (_: number, record: CurrentValuesDataInfo) => {
    const recordKey = findTrapCatchesKey(record)

    if (!recordKey) return translate.phrases.templates('-')

    const insectId = recordKey.match(/insectId(\d+)/)?.[1]

    return unitConverter
      .insectTrapCatches(record[`${prefix}_insectId${insectId}` as TInsectTrapCatchesKey])
      .valueWithSuffix()
  },
  sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) =>
    basicSort(pestSorter(a, prefix, 'insectTrapCatches_'), pestSorter(b, prefix, 'insectTrapCatches_')),
})

export const trapCatchesColumns: TValueGroupColumns = {
  getColumns: ({ isSouthernHemisphere }) => [
    {
      title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Pest Type')} />,
      dataIndex: 'pest_type',
      key: 'pest_type',
      width: 200,
      render: (_: number, record: CurrentValuesDataInfo) => getPestName(record),
      sorter: (a: CurrentValuesDataInfo, b: CurrentValuesDataInfo) =>
        (getPestName(a) || '').localeCompare(getPestName(b) || '', undefined, { sensitivity: 'base' }),
    },
    trapCatchesColumn('insectTrapCatches_todaySum', translate.phrases.banyanApp('Today')),
    trapCatchesColumn('insectTrapCatches_previous2DaysSum', lastDays(2)),
    trapCatchesColumn('insectTrapCatches_previous3DaysSum', lastDays(3)),
    trapCatchesColumn('insectTrapCatches_previous7DaysSum', lastDays(7)),
    trapCatchesColumn('insectTrapCatches_previous14DaysSum', lastDays(14)),
    trapCatchesColumn('insectTrapCatches_previous30DaysSum', lastDays(30)),
    trapCatchesColumn('insectTrapCatches_previous60DaysSum', lastDays(60)),
    trapCatchesColumn('insectTrapCatches_sinceBiofixSum', translate.phrases.banyanApp('Since Biofix')),
    isSouthernHemisphere
      ? trapCatchesColumn('insectTrapCatches_sinceJul1Sum', sinceFirstOf('Jul'))
      : trapCatchesColumn('insectTrapCatches_sinceJan1Sum', sinceFirstOf('Jan')),
  ],
}
